import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { dangNhapGoogle, theoDoiDangNhap } from '../lib/auth.js';
import { layDb } from '../lib/firebase.js';
import { docNhatKy } from '../lib/nhatKy.js';
import { docLuotTaiMau } from '../lib/templates.js';
import { tongHop, nhomTrang, NHAN_SU_KIEN, NGUONG_THOAT_NGAY } from '../lib/statsModel.js';
import { CAU_HINH_DO_LUONG } from '../lib/track.js';
import { MA_CLARITY } from '../lib/clarity.js';
import { boQuaTrinhDuyetNay, datBoQua } from '../lib/consent.js';
import { TOOLS } from '../data/tools.js';

/**
 * /admin/thong-ke — hành trình và tương tác của khách truy cập.
 *
 * Nguồn số liệu: nhật ký ẩn danh `su_kien` (chỉ có từ những người đã đồng ý
 * đo lường) và bộ đếm `template_downloads` (đếm mọi lượt tải mẫu). Báo cáo
 * sâu hơn — đường dẫn chi tiết, nhân khẩu, so sánh theo thời gian — xem trên
 * GA4 và Looker Studio; video phiên xem trên Clarity. Trang này là bảng điều
 * khiển hằng ngày, không thay các công cụ đó.
 */
const ENV = import.meta.env || {};
const LOOKER = /^https:\/\/lookerstudio\.google\.com\//.test(ENV.VITE_LOOKER_STUDIO_URL || '') ? ENV.VITE_LOOKER_STUDIO_URL : '';

function useQuyenQuanTri() {
  const [s, setS] = useState({ user: null, role: '', checking: true });
  useEffect(() => theoDoiDangNhap(async (next) => {
    setS({ user: next, role: '', checking: true });
    let role = '';
    if (next) try {
      const db = await layDb();
      const { doc, getDoc } = await import('firebase/firestore/lite');
      const snap = await getDoc(doc(db, 'admins', next.uid));
      role = snap.exists() ? snap.data()?.role || '' : '';
    } catch { role = ''; }
    setS({ user: next, role, checking: false });
  }), []);
  return { ...s, allowed: ['admin', 'editor'].includes(s.role) };
}

const TEN_CONG_CU = Object.fromEntries(TOOLS.flatMap((t) => [[t.toolId, t.title], [t.slug, t.title]]));
const so = (n) => new Intl.NumberFormat('vi-VN').format(Math.round(n || 0));
const pt = (x) => `${(100 * (x || 0)).toLocaleString('vi-VN', { maximumFractionDigits: 1 })}%`;
const thoiGian = (s) => (s < 60 ? `${s} giây` : `${Math.floor(s / 60)} phút ${s % 60 ? `${s % 60} giây` : ''}`.trim());
const gio = (t) => new Date(t).toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' });

function nhanTrang(path) {
  const m = /^\/cong-cu\/([^/]+)/.exec(path);
  if (m && TEN_CONG_CU[m[1]]) return TEN_CONG_CU[m[1]];
  if (path === '/') return 'Trang chủ';
  return null;
}

function Trang({ path }) {
  const nhan = nhanTrang(path);
  return <span className="tk-trang"><code>{path}</code>{nhan && <span className="nho"> {nhan}</span>}</span>;
}

function BangDem({ tieuDe, goiY, rows, cot = 'Lượt', render = (r) => r.ten, trong = 'Chưa có dữ liệu.' }) {
  const max = Math.max(1, ...rows.map((r) => r.so));
  return <section className="tk-khoi">
    <h2>{tieuDe}</h2>
    {goiY && <p className="nho">{goiY}</p>}
    {rows.length ? <table className="tk-bang"><thead><tr><th scope="col">Mục</th><th scope="col" className="so">{cot}</th></tr></thead>
      <tbody>{rows.map((r, i) => <tr key={i}>
        <td><div className="tk-thanh-nen"><div className="tk-thanh" style={{ width: `${(100 * r.so) / max}%` }} /></div>{render(r)}</td>
        <td className="so">{so(r.so)}</td></tr>)}</tbody></table>
      : <p className="nho">{trong}</p>}
  </section>;
}

function The({ nhan, giaTri, phu }) {
  return <div className="tk-the"><p className="tk-the-nhan">{nhan}</p><p className="tk-the-so">{giaTri}</p>{phu && <p className="nho">{phu}</p>}</div>;
}

function Phien({ p }) {
  const [mo, setMo] = useState(false);
  return <li className="tk-phien">
    <button type="button" className="tk-phien-dau" aria-expanded={mo} onClick={() => setMo(!mo)}>
      <span>{gio(p.batDau)}</span>
      <span>{p.thietBi === 'mobile' ? 'Điện thoại' : 'Máy tính'} · {p.nguon}</span>
      <span>{p.soTrang} trang · {thoiGian(p.thoiLuong)}</span>
      <span className={p.hoanThanh ? 'tk-nhan tot' : p.coTuongTac ? 'tk-nhan vua' : 'tk-nhan'}>{p.hoanThanh ? 'Hoàn thành' : p.coTuongTac ? 'Có tương tác' : 'Chỉ xem'}</span>
    </button>
    {mo && <ol className="tk-buoc">{p.buoc.map((b, i) => <li key={i}>
      <span className="nho">{new Date(b.t).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>{' '}
      {b.ten === 'page_view' ? <>Xem <Trang path={b.trang} /></> : <><strong>{NHAN_SU_KIEN[b.ten] || b.ten}</strong>{b.chiTiet && <> · <code>{b.chiTiet}</code></>}</>}
    </li>)}</ol>}
  </li>;
}

export default function StatsAdmin() {
  const { user, allowed, checking } = useQuyenQuanTri();
  const [soNgay, setSoNgay] = useState(7);
  const [data, setData] = useState({ banGhi: [], catBot: false });
  const [taiMau, setTaiMau] = useState([]);
  const [trangThai, setTrangThai] = useState('');
  const [boQua, setBoQua] = useState(false);

  useEffect(() => { setBoQua(boQuaTrinhDuyetNay()); }, []);

  async function tai(n = soNgay) {
    setTrangThai('Đang tải số liệu…');
    try {
      const [nk, mau] = await Promise.all([docNhatKy(n), docLuotTaiMau(2000).catch(() => [])]);
      setData(nk); setTaiMau(mau);
      setTrangThai(nk.catBot ? 'Chỉ hiển thị 5.000 bản ghi mới nhất; chọn khoảng ngày ngắn hơn để xem đủ.' : '');
    } catch (e) {
      setTrangThai(e?.code === 'permission-denied'
        ? 'Chưa đọc được nhật ký. Hãy deploy firestore.rules mới (npm run deploy) rồi tải lại trang.'
        : (e?.message || 'Không tải được số liệu.'));
    }
  }

  useEffect(() => { if (allowed) tai(soNgay); /* eslint-disable-next-line */ }, [allowed, soNgay]);

  const th = useMemo(() => tongHop(data.banGhi, { soNgay }), [data, soNgay]);

  if (checking) return <div className="surface section-space"><p>Đang kiểm tra quyền quản trị…</p></div>;
  if (!user) return <section className="surface admin-gate"><h1>Thống kê truy cập</h1><p>Đăng nhập bằng tài khoản đã được cấp quyền.</p><button className="nut" onClick={() => dangNhapGoogle()}>Đăng nhập với Google</button></section>;
  if (!allowed) return <section className="surface admin-gate"><h1>Chưa có quyền quản trị</h1><p>Tài khoản <strong>{user.email}</strong> chưa nằm trong danh sách admin.</p><p className="nho">UID để cấp quyền: <code>{user.uid}</code></p></section>;

  return <BangThongKe th={th} soNgay={soNgay} setSoNgay={setSoNgay} taiMau={taiMau} trangThai={trangThai}
    boQua={boQua} setBoQua={setBoQua} taiLai={() => tai()} />;
}

/** Phần hiển thị, tách riêng để test dựng được bằng dữ liệu mẫu (không cần đăng nhập). */
export function BangThongKe({ th, soNgay, setSoNgay, taiMau = [], trangThai = '', boQua = false, setBoQua = () => {}, taiLai = () => {} }) {
  const maxNgay = Math.max(1, ...th.theoNgay.map((d) => d.phien));
  const cfg = CAU_HINH_DO_LUONG;

  return <div className="tk">
    <header className="tk-dau">
      <div>
        <p className="nho"><Link to="/admin/playbook">Playbook</Link> · <Link to="/admin/mau">Mẫu</Link> · <Link to="/admin/nguon-von">Nguồn vốn</Link> · <Link to="/admin/chuong-trinh">Chương trình</Link></p>
        <h1>Thống kê truy cập</h1>
        <p className="nho">Chỉ tính khách đã đồng ý đo lường. Khách từ chối không xuất hiện ở đây, trừ bảng lượt tải mẫu.</p>
      </div>
      <div className="tk-dieu-khien">
        <div role="group" aria-label="Khoảng thời gian" className="tk-chon">
          {[1, 7, 30].map((n) => <button key={n} type="button" className={n === soNgay ? 'nut' : 'nut phu'} onClick={() => setSoNgay(n)}>{n === 1 ? 'Hôm nay' : `${n} ngày`}</button>)}
        </div>
        <button type="button" className="text-button" onClick={taiLai}>Tải lại</button>
      </div>
    </header>

    <section className="tk-cau-hinh">
      <span className={cfg.ga ? 'tk-nhan tot' : 'tk-nhan'}>GA4 {cfg.ga ? 'đang bật' : 'chưa bật'}</span>
      <span className={cfg.clarity ? 'tk-nhan tot' : 'tk-nhan'}>Clarity {cfg.clarity ? 'đang bật' : 'chưa có mã'}</span>
      <span className={cfg.nhatKy ? 'tk-nhan tot' : 'tk-nhan'}>Nhật ký nội bộ {cfg.nhatKy ? 'đang bật' : 'đã tắt'}</span>
      {cfg.ga && <a href="https://analytics.google.com/" target="_blank" rel="noreferrer">Mở GA4 ↗</a>}
      {MA_CLARITY && <a href={`https://clarity.microsoft.com/projects/view/${MA_CLARITY}/dashboard`} target="_blank" rel="noreferrer">Mở Clarity ↗</a>}
      <label className="tk-bo-qua">
        <input type="checkbox" checked={boQua} onChange={(e) => { datBoQua(e.target.checked); setBoQua(e.target.checked); }} />
        Không đếm lượt truy cập của trình duyệt này
      </label>
    </section>

    {trangThai && <p role="status" className="admin-message">{trangThai}</p>}

    <section className="tk-the-luoi">
      <The nhan="Phiên truy cập" giaTri={so(th.soPhien)} phu={`${so(th.luotXem)} lượt xem trang`} />
      <The nhan="Trang mỗi phiên" giaTri={th.trangMoiPhien.toLocaleString('vi-VN', { maximumFractionDigits: 1 })} phu={`Thời lượng trung vị ${thoiGian(th.thoiLuongTrungVi)}`} />
      <The nhan="Có tương tác thật" giaTri={pt(th.tyLeTuongTac)} phu="Dùng công cụ, đọc hết bài, tải mẫu…" />
      <The nhan="Hoàn thành" giaTri={pt(th.tyLeHoanThanh)} phu="Lưu, xuất file, tải mẫu, liên hệ quỹ" />
      <The nhan="Thoát ngay" giaTri={pt(th.tyLeThoatNgay)} phu={`Một trang, không tương tác, ở lại dưới ${NGUONG_THOAT_NGAY} giây`} />
    </section>

    <section className="tk-khoi">
      <h2>Phiên theo ngày</h2>
      <div className="tk-cot-luoi" role="img" aria-label="Biểu đồ số phiên theo ngày">
        {th.theoNgay.map((d) => <div key={d.ngay} className="tk-cot-o" title={`${d.ngay}: ${d.phien} phiên, ${d.luotXem} lượt xem`}>
          <span className="tk-cot-so">{d.phien || ''}</span>
          <div className="tk-cot" style={{ height: `${(100 * d.phien) / maxNgay}%` }} />
          <span className="tk-cot-nhan">{d.ngay.slice(8)}/{d.ngay.slice(5, 7)}</span>
        </div>)}
      </div>
    </section>

    <section className="tk-khoi">
      <h2>Phễu</h2>
      <p className="nho">Mỗi bước tính trên số phiên. Chỗ tụt mạnh nhất là chỗ nên sửa trước.</p>
      {th.pheu.map((b, i) => <div key={b.buoc} className="tk-pheu-hang">
        <span>{i + 1}. {b.buoc}</span>
        <div className="tk-thanh-nen lon"><div className="tk-thanh" style={{ width: `${th.soPhien ? (100 * b.so) / th.soPhien : 0}%` }} /></div>
        <span className="so">{so(b.so)}{i > 0 && th.pheu[i - 1].so ? <span className="nho"> ({pt(b.so / th.pheu[i - 1].so)})</span> : null}</span>
      </div>)}
    </section>

    <div className="tk-hai-cot">
      <BangDem tieuDe="Trang bắt đầu" goiY="Trang đầu tiên khách mở trong phiên." rows={th.trangVao} cot="Phiên" render={(r) => <Trang path={r.ten} />} />
      <BangDem tieuDe="Trang kết thúc" goiY="Trang cuối cùng trước khi rời đi." rows={th.trangThoat} cot="Phiên" render={(r) => <Trang path={r.ten} />} />
    </div>

    <BangDem tieuDe="Hành trình phổ biến" goiY="Bốn trang đầu của các phiên xem từ hai trang trở lên." rows={th.hanhTrinh} cot="Phiên"
      render={(r) => <span className="tk-duong">{r.ten.split(' → ').map((x, i) => <span key={i}>{i > 0 && ' → '}<code>{x}</code></span>)}</span>} />

    <section className="tk-khoi">
      <h2>Chất lượng theo nhóm nguồn</h2>
      <p className="nho">Thoát ngay = một trang, không thao tác, dưới {NGUONG_THOAT_NGAY} giây. Hai cột cuối chỉ tính phiên bắt đầu ở /nguon-von, để so trước và sau khi sửa trang này.</p>
      {(th.theoLoaiNguon || []).length ? <table className="tk-bang"><thead><tr><th scope="col">Nhóm</th><th className="so" scope="col">Phiên</th><th className="so" scope="col">Thời lượng trung vị</th><th className="so" scope="col">Thoát ngay</th><th className="so" scope="col">Trang mỗi phiên</th><th className="so" scope="col">Vào /nguon-von</th><th className="so" scope="col">Thời lượng ở /nguon-von</th></tr></thead>
        <tbody>{th.theoLoaiNguon.map((r) => <tr key={r.loai}><td>{r.loai}</td><td className="so">{so(r.so)}</td><td className="so">{thoiGian(r.thoiLuongTrungVi)}</td><td className="so">{pt(r.tyLeThoatNgay)}</td><td className="so">{r.trangMoiPhien.toLocaleString('vi-VN', { maximumFractionDigits: 1 })}</td><td className="so">{so(r.vaoNguonVon)}</td><td className="so">{r.vaoNguonVon ? thoiGian(r.thoiLuongNguonVon) : 'chưa có'}</td></tr>)}</tbody></table>
        : <p className="nho">Chưa có dữ liệu.</p>}
    </section>

    <div className="tk-hai-cot">
      <BangDem tieuDe="Nguồn truy cập" rows={th.nguon} cot="Phiên" render={(r) => <>{r.ten} <span className="nho">· {r.loai}</span></>} />
      <BangDem tieuDe="Thiết bị" rows={th.thietBi} cot="Phiên" />
    </div>

    <section className="tk-khoi">
      <h2>Công cụ</h2>
      <p className="nho">Bắt đầu = lần đầu sửa một ô. Lưu = bấm lưu bản nháp hoặc kịch bản. Tỷ lệ lưu thấp nghĩa là người dùng bỏ dở giữa chừng.</p>
      {th.congCu.length ? <table className="tk-bang"><thead><tr><th scope="col">Công cụ</th><th className="so" scope="col">Bắt đầu</th><th className="so" scope="col">Lưu</th><th className="so" scope="col">Xuất file</th><th className="so" scope="col">Tỷ lệ lưu</th></tr></thead>
        <tbody>{th.congCu.map((c) => <tr key={c.ten}><td>{TEN_CONG_CU[c.ten] || c.ten}</td><td className="so">{so(c.batDau)}</td><td className="so">{so(c.luu)}</td><td className="so">{so(c.xuat)}</td><td className="so">{c.batDau ? pt(c.luu / c.batDau) : '—'}</td></tr>)}</tbody></table>
        : <p className="nho">Chưa có dữ liệu.</p>}
    </section>

    <div className="tk-hai-cot">
      <BangDem tieuDe={`Tải mẫu trong ${soNgay === 1 ? 'hôm nay' : `${soNgay} ngày`}`} goiY="Từ khách đã đồng ý đo lường." rows={th.mauTai} />
      <BangDem tieuDe="Tải mẫu, mọi thời điểm" goiY="Bộ đếm riêng, tính cả khách từ chối đo lường. 2.000 lượt gần nhất." rows={taiMau.map((m) => ({ ten: m.slug, so: m.soLuot }))} />
    </div>

    <div className="tk-hai-cot">
      <BangDem tieuDe="Bài Playbook được đọc hết" goiY="Cuộn qua 75% bài." rows={th.baiDocHet} render={(r) => <Link to={`/playbook/${r.ten}`}>{r.ten}</Link>} />
      <BangDem tieuDe="Trang được xem nhiều" rows={th.trangXem} render={(r) => <><Trang path={r.ten} /> <span className="nho">· {nhomTrang(r.ten)}</span></>} />
    </div>

    <div className="tk-hai-cot">
      <BangDem tieuDe="Liên kết ra ngoài" goiY="Tên miền khách bấm sang (trang quỹ, nguồn trích dẫn)." rows={th.lienKetNgoai} />
      <BangDem tieuDe="Tất cả sự kiện" rows={th.suKien} render={(r) => r.nhan} />
    </div>

    <section className="tk-khoi">
      <h2>Phiên gần đây</h2>
      <p className="nho">Bấm vào một phiên để xem từng bước. Muốn xem lại thao tác dạng video, mở Clarity.</p>
      {th.phienGanDay.length ? <ul className="tk-ds-phien">{th.phienGanDay.map((p) => <Phien key={p.id} p={p} />)}</ul> : <p className="nho">Chưa có phiên nào trong khoảng này.</p>}
    </section>

    {LOOKER && <section className="tk-khoi">
      <h2>Báo cáo GA4 (Looker Studio)</h2>
      <div className="tk-looker"><iframe title="Báo cáo Looker Studio" src={LOOKER} loading="lazy" allowFullScreen sandbox="allow-storage-access-by-user-activation allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox" /></div>
    </section>}
  </div>;
}
